import { Vector3 } from "three";
import SceneObject from "./SceneObject";
import ExtrudedLoopAnnotationData from "../sceneObjectData/ExtrudedLoopAnnotationData";
import EditHandleData from "../sceneObjectData/EditHandleData";
import EditHandleTypeEnum from "../../enums/EditHandleTypeEnum";
import CommandIdEnum from "../../enums/CommandIdEnums";
import PropertyList from "../properties/PropertyList";
import { PropertyId } from "../properties/PropertyId";
import PropertyItem from "../properties/PropertyItem";
import { PID_FONT_SIZE, PID_SHOW_AREA_TEXT, PID_EXTRUSION_DEPTH, PID_SHOW_VOLUME } from "../properties/PropertyIds";
import { CATEGORY_COMMON, CATEGORY_DIMENSIONS } from "../properties/PropertyCategoryRegistry";
import lengthCmTranslator from "../properties/translators/lengthCmTranslator";
import booleanTranslator from "../properties/translators/booleanTranslator";
import extrusionDepthCmTranslator from "../properties/translators/extrusionDepthCmTranslator";
import Constants from "../../constants";
import { sceneUnitsFromMm } from "../../utils/scaler";
import { isViewAlongAxis } from "../../utils/editHandleVisibility";

// Shared font-size property handling and depth edit handle for any annotation extruded from a
// closed loop. Shape-specific fields (rectangle width/height, circle radius, polygon vertices)
// and their own edit handles are added by each leaf SceneObject subclass, which must merge them
// with super.getEditHandles() to keep this shared depth handle.
export default class ExtrudedLoopAnnotationSceneObject<T extends ExtrudedLoopAnnotationData> extends SceneObject<T> {

    getProperties(props: PropertyList, filterId?: PropertyId): void {
        super.getProperties(props, filterId);

        const fontSize = props.getOrCreateProperty(PID_FONT_SIZE, () => new PropertyItem(PID_FONT_SIZE, lengthCmTranslator));
        fontSize.value = this.data.fontSize;
        fontSize.categoryId = CATEGORY_COMMON.id;

        const showAreaText = props.getOrCreateProperty(PID_SHOW_AREA_TEXT, () => new PropertyItem(PID_SHOW_AREA_TEXT, booleanTranslator));
        showAreaText.value = this.data.showAreaText;
        showAreaText.categoryId = CATEGORY_COMMON.id;

        const extrusionDepth = props.getOrCreateProperty(PID_EXTRUSION_DEPTH, () => new PropertyItem(PID_EXTRUSION_DEPTH, extrusionDepthCmTranslator));
        extrusionDepth.value = this.data.depth;
        extrusionDepth.categoryId = CATEGORY_DIMENSIONS.id;

        const showVolume = props.getOrCreateProperty(PID_SHOW_VOLUME, () => new PropertyItem(PID_SHOW_VOLUME, booleanTranslator));
        showVolume.value = this.data.showVolume;
        showVolume.categoryId = CATEGORY_COMMON.id;
    }

    setProperties(props: PropertyList): void {
        super.setProperties(props);

        const fontSize = props.getProperty<number>(PID_FONT_SIZE);
        if (fontSize) this.data.fontSize = fontSize.value;

        const showAreaText = props.getProperty<boolean>(PID_SHOW_AREA_TEXT);
        if (showAreaText) this.data.showAreaText = showAreaText.value;

        const extrusionDepth = props.getProperty<number>(PID_EXTRUSION_DEPTH);
        if (extrusionDepth) this.data.depth = extrusionDepth.value;

        const showVolume = props.getProperty<boolean>(PID_SHOW_VOLUME);
        if (showVolume) this.data.showVolume = showVolume.value;

        this.geometry.update(this.data, false);
    }

    getEditHandles(localViewDirection: Vector3): EditHandleData[] {
        const data = this.data;
        const axis = data.placement.axis.clone().normalize();
        const minDepth = sceneUnitsFromMm(Constants.MIN_EXTRUSION_DEPTH_MM);

        // Looking along the extrusion axis, a depth change is invisible — offer no handle for it.
        if (isViewAlongAxis(localViewDirection, axis)) return [];

        return [
            new EditHandleData(data.uuid, EditHandleTypeEnum.EXTRUSION_DEPTH,
                data.getTopFaceCentroid3d(), CommandIdEnum.ExtrudedLoopAreaSetExtrusionDepth,
                (d, delta) => {
                    const extrudedData = d as ExtrudedLoopAnnotationData;
                    const newDepth = extrudedData.depth + delta.dot(axis);
                    // Depth can be negative (extrusion flips to the opposite side of the profile)
                    // — only the magnitude is floored, so dragging through zero flips the sign
                    // once the drag has gone far enough past it, rather than getting stuck at zero.
                    const sign = newDepth < 0 ? -1 : 1;
                    extrudedData.depth = sign * Math.max(minDepth, Math.abs(newDepth));
                },
                undefined, axis)
        ];
    }
}
